import { Injectable } from '@angular/core';
import { LocaleService } from './locale.service';
import { PKCE_RETURN_URL_KEY, PKCE_STATE_KEY, PKCE_VERIFIER_KEY } from './keycloak-pkce.service';

/** localStorage keys holding the logged-in user's identity. */
const USER_LOCAL_KEYS = ['token', 'jwt', 'userId', 'userName'];

/** localStorage key prefixes of per-user caches (company graph exploration, any version). */
const USER_LOCAL_PREFIXES = ['companyGraph.'];

/**
 * sessionStorage keys tied to the logged-in user's flows: signup/OTP email, order & checkout,
 * an unfinished Keycloak login, and the One Tap flag (so One Tap can prompt again after logout).
 */
const USER_SESSION_KEYS = [
    'email',
    'orderNumber',
    'purchaseCode',
    'packageCode',
    'hpcOrders',
    'hpcOrgDrafts',
    PKCE_VERIFIER_KEY,
    PKCE_STATE_KEY,
    PKCE_RETURN_URL_KEY,
    'googleOneTapPrompted',
];

/**
 * Wipes the logged-in user's data from the browser on logout / session expiry.
 * Non-user data is kept on purpose (locale, publicIp, navigation flags, dev flags such as
 * enableDebug, and third-party keys like Google Ads' _gcl_ls), so never use localStorage.clear().
 */
@Injectable({
    providedIn: 'root'
})
export class UserSessionService {

    constructor(private localeService: LocaleService) {
    }

    /** Removes the user's identity and per-user caches from localStorage only (guest flows in sessionStorage stay). */
    clearLocal(): void {
        USER_LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));
        Object.keys(localStorage)
            .filter((key) => USER_LOCAL_PREFIXES.some((prefix) => key.startsWith(prefix)))
            .forEach((key) => localStorage.removeItem(key));
    }

    /** Clears all of the user's data from localStorage and sessionStorage. */
    clear(): void {
        this.clearLocal();
        USER_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
    }

    /** Clears all of the user's data, then fully reloads the home page so no in-memory state survives. */
    clearAndReload(): void {
        this.clear();
        window.location.href = `/${this.localeService.getCurrentLocale()}`;
    }
}
